import React, { useCallback, useMemo, useState } from 'react';
import { DashboardLayout } from '../components/layout';
import { useAdminNavigation } from '../hooks/useAdminNavigation';
import * as adminApi from '../../../api/adminApi';

/**
 * Add or correct a regulation by hand.
 *
 * Nothing in the product could do this. The seeded jurisdictions were inserted
 * by a script run against the database in April 2026 — every one carries the
 * same timestamp to the second — and when nine Peruvian titles turned out to
 * hold base64 identifiers, there was no screen on which anyone could retype
 * them; it took a deployment.
 *
 * Deliberately metadata and a link, not documents: full text arrives the way it
 * does for every other source, by the link backfill following the URL. An
 * import is previewed before it commits, and a sheet with any bad row writes
 * nothing at all — a half-applied import is the worst outcome, because nobody
 * can tell what landed and re-running it duplicates what did.
 */
const FIELDS: { key: keyof adminApi.ManualRegulationInput; label: string; hint?: string; required?: boolean }[] = [
  { key: 'jurisdiction', label: 'Jurisdiction', hint: 'Name or code, exactly as under Jurisdictions', required: true },
  { key: 'title', label: 'Title', required: true },
  { key: 'document_no', label: 'Document number', hint: 'Identifies the instrument on a re-import' },
  { key: 'document_type', label: 'Type', hint: 'Law, Decree, Bill, Regulation…' },
  { key: 'status', label: 'Status', hint: 'Effective, Draft, Repealed…' },
  { key: 'effective_date', label: 'Effective date', hint: 'YYYY-MM-DD' },
  { key: 'last_action_date', label: 'Last action date', hint: 'YYYY-MM-DD' },
  { key: 'source_url', label: 'Official source URL', hint: 'Where the text can be read — the fetcher follows this' },
  { key: 'summary', label: 'Summary' },
];

const EMPTY: adminApi.ManualRegulationInput = { jurisdiction: '', title: '' };

const AddRegulationsPage: React.FC = () => {
  const {
    sidebarCollapsed, setSidebarCollapsed, activeSection,
    setSelectedRegulation, handleNavClick, handleLogout,
  } = useAdminNavigation();

  const [form, setForm] = useState<adminApi.ManualRegulationInput>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<adminApi.ManualRowResult | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const [csv, setCsv] = useState('');
  const [preview, setPreview] = useState<adminApi.ManualImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  const token = window.localStorage.getItem('policyora_token');
  const canSave = Boolean(form.jurisdiction.trim() && form.title.trim());

  const submit = useCallback(() => {
    if (!token || !canSave) return;
    setSaving(true); setFormError(null); setSaved(null);
    adminApi.addRegulation(token, form)
      .then((r) => { setSaved(r); setForm(EMPTY); })
      .catch((err) => setFormError(err.message || 'Could not add the regulation'))
      .finally(() => setSaving(false));
  }, [token, form, canSave]);

  const runImport = useCallback((dryRun: boolean) => {
    if (!token || !csv.trim()) return;
    setImporting(true); setImportError(null);
    adminApi.importRegulations(token, csv, dryRun)
      .then((r) => { setPreview(r); if (r.committed) setCsv(''); })
      .catch((err) => setImportError(err.message || 'Could not read that sheet'))
      .finally(() => setImporting(false));
  }, [token, csv]);

  // Committing is offered only once a preview has been seen and is clean:
  // the point of the preview is that nobody commits a sheet they have not read.
  const readyToCommit = useMemo(
    () => Boolean(preview && preview.dry_run && preview.tally.errors === 0 && preview.tally.rows > 0),
    [preview],
  );

  const label: React.CSSProperties = { fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ds-text-muted)' };
  const input: React.CSSProperties = { width: '100%', padding: '0.45rem 0.6rem', borderRadius: 8, border: '1px solid var(--ds-border)', background: 'var(--ds-surface)', color: 'var(--ds-text)', fontSize: '0.88rem' };
  const card: React.CSSProperties = { border: '1px solid var(--ds-border)', borderRadius: 12, padding: '1rem 1.15rem', marginBottom: 18 };
  const button: React.CSSProperties = { padding: '0.45rem 0.95rem', borderRadius: 8, border: '1px solid var(--ds-border-strong)', background: 'var(--ds-surface)', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 600, color: 'var(--ds-text)' };
  const th: React.CSSProperties = { textAlign: 'left', padding: '0.5rem 0.7rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--ds-text-muted)', borderBottom: '1px solid var(--ds-border)' };
  const td: React.CSSProperties = { padding: '0.5rem 0.7rem', fontSize: '0.84rem', color: 'var(--ds-text)', borderBottom: '1px solid var(--ds-surface-2)' };

  return (
    <DashboardLayout
      collapsed={sidebarCollapsed}
      activeSection={activeSection}
      welcomeLabel="Add Regulations"
      selectedRegulation={null}
      onToggleSidebar={() => setSidebarCollapsed((prev) => !prev)}
      onNavClick={handleNavClick}
      onProfileClick={() => handleNavClick('profile')}
      onLogout={handleLogout}
      onRegulationModalClose={() => setSelectedRegulation(null)}
    >
      <section style={{ padding: '1.25rem 1.5rem', maxWidth: 1100 }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 800, color: 'var(--ds-text)' }}>Add Regulations</h2>
        <p style={{ margin: '4px 0 18px', color: 'var(--ds-text-muted)', fontSize: '0.88rem' }}>
          Enter an instrument the platform does not yet hold, or correct one that is wrong. Records are
          stored as added by hand, so they stay distinguishable from pipeline data. Give the official
          source URL and the text will be fetched from it in the normal way — nothing is uploaded here.
        </p>

        {/* ── one record ─────────────────────────────────────────────── */}
        <div style={card} data-testid="single-form">
          <div style={{ fontWeight: 700, color: 'var(--ds-text)', marginBottom: 10 }}>One regulation</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label style={label} htmlFor={`f-${f.key}`}>
                  {f.label}{f.required ? ' *' : ''}
                </label>
                <input
                  id={`f-${f.key}`}
                  style={input}
                  value={(form[f.key] as string) || ''}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                />
                {f.hint && <div style={{ fontSize: '0.7rem', color: 'var(--ds-text-faint)', marginTop: 2 }}>{f.hint}</div>}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button type="button" style={{ ...button, opacity: canSave && !saving ? 1 : 0.55 }}
                    disabled={!canSave || saving} onClick={submit}>
              {saving ? 'Saving…' : 'Add regulation'}
            </button>
            {saved && (
              <span style={{ fontSize: '0.84rem', color: 'var(--ds-success)' }} data-testid="save-result">
                {saved.action === 'create'
                  ? `Added "${saved.title}" under ${saved.jurisdiction}.`
                  : `Corrected "${saved.title}" — it already existed under ${saved.jurisdiction}.`}
              </span>
            )}
            {formError && <span style={{ fontSize: '0.84rem', color: 'var(--ds-danger)' }} data-testid="save-error">{formError}</span>}
          </div>
        </div>

        {/* ── a sheet ────────────────────────────────────────────────── */}
        <div style={card} data-testid="import-form">
          <div style={{ fontWeight: 700, color: 'var(--ds-text)' }}>Several at once</div>
          <div style={{ fontSize: '0.78rem', color: 'var(--ds-text-muted)', margin: '2px 0 10px' }}>
            Paste a CSV whose first row names the columns: <code>jurisdiction, title, document_type,
            document_no, status, source_url, summary, effective_date, last_action_date</code>. Only
            jurisdiction and title are required. Check it first — a sheet with any bad row writes nothing.
          </div>
          <textarea
            aria-label="CSV"
            value={csv}
            onChange={(e) => { setCsv(e.target.value); setPreview(null); }}
            rows={6}
            style={{ ...input, fontFamily: 'ui-monospace, monospace', fontSize: '0.8rem' }}
          />
          <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" style={{ ...button, opacity: csv.trim() && !importing ? 1 : 0.55 }}
                    disabled={!csv.trim() || importing} onClick={() => runImport(true)}>
              {importing ? 'Checking…' : 'Check the sheet'}
            </button>
            <button type="button"
                    style={{ ...button, opacity: readyToCommit && !importing ? 1 : 0.55 }}
                    disabled={!readyToCommit || importing} onClick={() => runImport(false)}>
              Commit
            </button>
            {importError && <span style={{ fontSize: '0.84rem', color: 'var(--ds-danger)' }} data-testid="import-error">{importError}</span>}
          </div>

          {preview && (
            <div style={{ marginTop: 14 }} data-testid="import-preview">
              <div style={{ fontSize: '0.85rem', color: preview.tally.errors ? 'var(--ds-danger)' : 'var(--ds-text-secondary)', marginBottom: 8 }}>
                {preview.committed
                  ? `Committed — ${preview.tally.created} added, ${preview.tally.updated} corrected.`
                  : preview.tally.errors
                    ? `${preview.tally.errors} of ${preview.tally.rows} rows cannot be applied. Nothing has been written; fix them and check again.`
                    : `${preview.tally.rows} rows read and none rejected. Nothing is written until you commit.`}
              </div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr><th style={th}>Row</th><th style={th}>Action</th><th style={th}>Jurisdiction</th><th style={th}>Title</th><th style={th}>Problem</th></tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.row}>
                        <td style={td}>{row.row}</td>
                        <td style={{ ...td, color: row.action === 'error' ? 'var(--ds-danger)' : 'var(--ds-text)' }}>
                          {row.action === 'create' ? 'Add' : row.action === 'update' ? 'Correct' : 'Cannot apply'}
                        </td>
                        <td style={td}>{row.jurisdiction || '—'}</td>
                        <td style={{ ...td, whiteSpace: 'normal', maxWidth: 380 }}>{row.title || '—'}</td>
                        <td style={{ ...td, whiteSpace: 'normal', color: 'var(--ds-danger-text)' }}>{row.error || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>
    </DashboardLayout>
  );
};

export default AddRegulationsPage;
